import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import MaterialTable from 'material-table';
import { createTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';

import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';

import { AuthorizeContext } from '../RootContext';
import { api_url } from '../authorize';
import logo from './../img/lento_logo_transparent.png';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const useStyles = makeStyles((muiTheme) => ({
  page: {
    minHeight: 'calc(100vh - 72px)',
    padding: '28px 20px 44px',
    boxSizing: 'border-box',
    background: '#f3f7f9',
    color: '#173247',
    fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif',
    [muiTheme.breakpoints.down('sm')]: { padding: '20px 16px 32px' },
  },
  shell: { width: '100%', maxWidth: 'none', margin: 0 },
  hero: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 20,
    marginBottom: 20,
    padding: 0,
    border: 'none',
    borderRadius: 0,
    background: 'transparent',
    boxShadow: 'none',
    [muiTheme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: 12,
    },
  },
  eyebrow: {
    margin: 0,
    color: '#0b819d',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
  },
  title: {
    margin: '5px 0 0',
    color: '#173247',
    fontSize: 32,
    lineHeight: 1.15,
    fontWeight: 800,
    letterSpacing: '-0.025em',
    [muiTheme.breakpoints.down('xs')]: { fontSize: 27 },
  },
  subtitle: {
    maxWidth: 760,
    margin: '8px 0 0',
    color: '#607888',
    fontSize: 16,
    lineHeight: 1.55,
    fontWeight: 500,
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    minHeight: 0,
    padding: 0,
    borderRadius: 0,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    color: '#607888',
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    textAlign: 'left',
    '&::before': {
      content: '""',
      width: 7,
      height: 7,
      flex: '0 0 7px',
      borderRadius: '50%',
      background: '#0b819d',
    },
  },
  countValue: { display: 'inline', fontSize: 14, lineHeight: 1.4, fontWeight: 700 },
  countLabel: {
    display: 'inline',
    marginTop: 0,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: 'none',
  },
  tableCard: {
    width: '100%',
    overflow: 'hidden',
    borderRadius: 16,
    border: '1px solid #dce7ec',
    background: '#ffffff',
    boxShadow: '0 16px 36px rgba(13, 46, 67, 0.08)',
    '& > .MuiPaper-root, & .MuiPaper-root': {
      boxShadow: 'none !important',
      borderRadius: '0 !important',
    },
    '& .MuiToolbar-root': {
      minHeight: '68px !important',
      padding: '10px 18px !important',
      borderBottom: '1px solid #e2ebef',
      background: '#ffffff',
    },
    '& .MuiTableContainer-root': {
      width: '100% !important',
      maxWidth: '100% !important',
      overflowX: 'hidden !important',
    },
    '& .MuiTable-root': {
      width: '100% !important',
      maxWidth: '100% !important',
      minWidth: '0 !important',
      tableLayout: 'fixed !important',
      fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif !important',
    },
    '& .MuiTableCell-root': {
      boxSizing: 'border-box !important',
      minWidth: '0 !important',
      paddingLeft: '7px !important',
      paddingRight: '7px !important',
      overflow: 'hidden !important',
      fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif !important',
    },
    '& .MuiTableCell-head': {
      paddingTop: '12px !important',
      paddingBottom: '12px !important',
      background: '#f1f6f8 !important',
      color: '#173247 !important',
      borderBottom: '1px solid #dce7ec !important',
      fontSize: '13px !important',
      fontWeight: '700 !important',
      lineHeight: '1.2 !important',
      whiteSpace: 'normal !important',
      wordBreak: 'normal !important',
    },
    '& .MuiTableCell-body': {
      paddingTop: '11px !important',
      paddingBottom: '11px !important',
      borderBottom: '1px solid #edf1f3 !important',
      color: '#244154 !important',
      fontSize: '14px !important',
      fontWeight: '600 !important',
      lineHeight: '1.3 !important',
      verticalAlign: 'middle',
      whiteSpace: 'normal !important',
      overflowWrap: 'anywhere !important',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: '#f5fafb !important',
    },
    '& .MuiInputBase-root': {
      color: '#244154',
      fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif',
      fontSize: 14,
    },
    '& .MuiTablePagination-root': {
      borderTop: '1px solid #e2ebef',
      background: '#ffffff',
      color: '#607888',
    },
    '& .MuiIconButton-root': { color: '#49647d' },
    '& .MuiIconButton-root:hover': { background: '#eef7f9', color: '#0b819d' },
  },
  caseLink: {
    display: 'inline-block',
    maxWidth: '100%',
    overflowWrap: 'anywhere',
    color: '#176B5B',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    fontSize: 'inherit',
    fontWeight: 700,
    textDecoration: 'underline',
    textDecorationColor: 'rgba(23, 107, 91, 0.42)',
    textDecorationThickness: '1px',
    textUnderlineOffset: '2px',
    '&:hover': { color: '#0F5B4D', textDecorationColor: '#0F5B4D' },
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
                 minWidth: 0,
    padding: '5px 8px',
    boxSizing: 'border-box',
    borderRadius: 999,
    fontSize: 10.5,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    color: '#9b3e36',
    background: '#fff2f0',
    border: '1px solid #efc9c4',
  },
  loadingScreen: {
    position: 'fixed', top: 72, right: 0, bottom: 0, left: 0, zIndex: 1200,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    background: '#ffffff',
  },
  loadingLogoWrap: {
    width: 'min(680px, 62vw)',
    maxWidth: 'calc(100vw - 48px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    [muiTheme.breakpoints.down('sm')]: { width: 'min(520px, 78vw)' },
    [muiTheme.breakpoints.down('xs')]: { width: 'min(390px, 86vw)' },
  },
  loadingLogo: { display: 'block', width: '100%', height: 'auto', objectFit: 'contain', background: 'transparent', filter: 'none' },
  errorCard: {
    maxWidth: 600,
    margin: '70px auto',
    padding: 28,
    borderRadius: 18,
    background: '#ffffff',
    border: '1px solid #efc9c4',
    boxShadow: '0 18px 48px rgba(26, 53, 80, 0.09)',
    color: '#785a58',
    fontSize: 14,
    lineHeight: 1.55,
    textAlign: 'center',
  },
}));

const theme = createTheme({
  palette: {
    primary: { main: '#0b819d' },
    secondary: { main: '#18a39a' },
    background: { default: '#f4f7fb', paper: '#ffffff' },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    h6: { fontWeight: 700 },
  },
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 9,
      },
    },
    MuiIconButton: {
      root: { borderRadius: 9 },
    },
    MuiPaper: {
      rounded: { borderRadius: 14 },
    },
  },
});

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY') : String(value);
};

const formatName = (person) => {
  if (!person || typeof person !== 'object') return '—';
  return [person.Last_Name, person.First_Name].filter(Boolean).join(', ') || '—';
};

const AbortList = () => {
  const classes = useStyles();
  const { token, role, setAuthBody, setAuthenticated } = useContext(AuthorizeContext);
  const { surgeonid } = useParams();

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const roleName = String(role || '');
  const roleIncludes = (value) => roleName.includes(value);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        let url;

        if (roleIncludes('admin')) {
          url = `https://${api_url}/api/case_list?status=abort`;
        } else if (roleIncludes('hospital')) {
          url = `https://${api_url}/api/hospital_list?for_hospital=true&status=abort`;
        } else if (roleIncludes('distributor')) {
          url = `https://${api_url}/api/distributor_list?for_distributor=true&status=abort`;
        } else {
          url = `https://${api_url}/api/case_list?surgeon=${surgeonid || ''}&status=abort`;
        }

        const response = await axios({
          url,
          method: 'get',
          headers: {
            'x-access-token': token,
          },
        });

        if (!isMounted) return;

        if (response.data && response.data.notLoggedIn) {
          setAuthenticated(false);
          setAuthBody({ role: 'visitor' });
          alert('You have been logged out due to inactivity. Please log in again.');
          return;
        }

        if (response.data && response.data.message) {
          setLoadError(response.data.message);
          return;
        }

        setTableData(Array.isArray(response.data) ? response.data : []);
      } catch (requestError) {
        if (!isMounted) return;

        setLoadError(
          (requestError.response
            && requestError.response.data
            && requestError.response.data.message)
            || requestError.message
            || 'Unable to load aborted cases.'
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [role, setAuthBody, setAuthenticated, surgeonid, token]);

  const columns = useMemo(() => [
    {
      title: 'Case #',
      field: 'case_id',
      editable: 'never',
      render: (rowData) => (
        <Link className={classes.caseLink} to={`/case/${rowData.case_id}`}>
          {rowData.case_id || '—'}
        </Link>
      ),
      cellStyle: {
        width: 130,
      },
    },
    {
      title: 'Surgeon Name',
      field: 'Surgeon.Last_Name',
      editable: 'never',
      render: (rowData) => formatName(rowData.Surgeon),
    },
    {
      title: 'Patient Name',
      field: 'Patient.Last_Name',
      editable: 'never',
      render: (rowData) => formatName(rowData.Patient),
    },
    {
      title: 'DOPO',
      field: 'dateCreated',
      editable: 'never',
      type: 'date',
      render: (rowData) => formatDate(rowData.dateCreated),
      customSort: (a, b) => moment(a.dateCreated).valueOf() - moment(b.dateCreated).valueOf(),
      cellStyle: {
        whiteSpace: 'nowrap',
      },
    },
    {
      title: 'DOS',
      field: 'Patient.Date_of_Surgery',
      editable: 'never',
      type: 'date',
      defaultSort: roleIncludes('surgeon') ? 'asc' : undefined,
      render: (rowData) => formatDate(rowData.Patient && rowData.Patient.Date_of_Surgery),
      customSort: (a, b) => {
        const aDate = a.Patient && a.Patient.Date_of_Surgery;
        const bDate = b.Patient && b.Patient.Date_of_Surgery;
        return moment(aDate).valueOf() - moment(bDate).valueOf();
      },
      cellStyle: {
        whiteSpace: 'nowrap',
      },
    },
    {
      title: 'Surgery Type',
      field: 'Patient.Surgery_Type',
      editable: 'never',
      render: (rowData) => (rowData.Patient && rowData.Patient.Surgery_Type) || '—',
    },
    {
      title: 'Surgery Side',
      field: 'Patient.Side_of_Body',
      editable: 'never',
      render: (rowData) => (rowData.Patient && rowData.Patient.Side_of_Body) || '—',
    },
    {
      title: 'Status',
      field: 'case_status',
      editable: 'never',
      export: false,
      lookup: {
        13: 'Aborted',
        Abort: 'Aborted',
        aborted: 'Aborted',
      },
      render: () => <span className={classes.statusBadge}>Aborted</span>,
      cellStyle: {
        textAlign: 'center',
        width: 145,
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
  ], [classes.caseLink, classes.statusBadge, role]);

  if (loading) {
    return (
      <div className={classes.loadingScreen}>
        <div className={classes.loadingLogoWrap}>
          <img
            className={classes.loadingLogo}
            src={logo}
            alt="Lento Medical Innovation"
          />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <main className={classes.page}>
        <div className={classes.errorCard}>{loadError}</div>
      </main>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <main className={classes.page}>
        <div className={classes.shell}>
          <section className={classes.hero}>
            <div>
              <p className={classes.eyebrow}>Case archive</p>
              <h1 className={classes.title}>Aborted Cases</h1>
              <p className={classes.subtitle}>
                Search, filter, and review cases removed from the active workflow.
              </p>
            </div>

            <div className={classes.countBadge} aria-label={`${tableData.length} aborted cases`}>
              <span className={classes.countValue}>{tableData.length}</span>
              <span className={classes.countLabel}>Aborted</span>
            </div>
          </section>

          <section className={classes.tableCard} aria-label="Aborted case list">
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e2ebef', background: '#ffffff' }}>
              <p style={{ margin: 0, color: '#173247', fontSize: 14, fontWeight: 800 }}>Aborted case records</p>
              <p style={{ margin: '3px 0 0', color: '#718897', fontSize: 13, fontWeight: 500 }}>Search, sort, filter, and review case details from one place.</p>
            </div>
            <MaterialTable
              title=""
              columns={columns}
              data={tableData}
              icons={tableIcons}
              localization={{
                pagination: {
                  labelDisplayedRows: '{from}-{to} of {count}',
                  labelRowsSelect: 'rows',
                },
                toolbar: {
                  searchPlaceholder: 'Search aborted cases',
                  nRowsSelected: '{0} row(s) selected',
                },
                body: {
                  emptyDataSourceMessage: 'No aborted cases to display',
                  filterRow: {
                    filterTooltip: 'Filter',
                  },
                },
              }}
              options={{
                sorting: true,
                filtering: true,
                search: true,
                searchFieldAlignment: 'right',
                searchFieldVariant: 'standard',
                pageSize: 20,
                pageSizeOptions: [10, 20, 30, 50],
                paging: true,
                columnsButton: true,
                exportButton: true,
                exportAllData: true,
                showTitle: false,
                draggable: false,
                emptyRowsWhenPaging: false,
                tableLayout: 'fixed',
                headerStyle: {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  padding: '12px 7px',
                  background: '#f1f6f8',
                  color: '#173247',
                  borderBottom: '1px solid #dfcfd2',
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                },
                filterCellStyle: {
                  padding: '9px 7px',
                  background: '#ffffff',
                  fontSize: 14,
                },
                cellStyle: {
                  padding: '12px 7px',
                  color: '#264355',
                  fontSize: 13,
                  lineHeight: 1.5,
                  verticalAlign: 'middle',
                },
                rowStyle: (rowData, index) => ({
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#fbfafb',
                }),
              }}
              style={{
                width: '100%',
                 minWidth: 0,
                borderRadius: 0,
                boxShadow: 'none',
              }}
            />
          </section>
        </div>
      </main>
    </ThemeProvider>
  );
};

export default AbortList;